import React, {Fragment, useEffect, useState} from 'react';
import i18n from 'i18next';
import {useForm, usePage} from '@inertiajs/inertia-react';
import bffApiUrlService from "@/Utilities/BffApiUrlService";
import Button from '@/Components/Button';
import ModuleCard from "@/Components/ModuleCard";
import OnOffSwitch from '@/Components/ToggleCheckbox';
import DeletePerson from "@/Components/Modals/DeletePerson";
import {FormCheckbox, FormInput, PhoneOrEmailInput} from "@/Components/Controls";
import ValidationErrors from "@/Components/ValidationErrors";
import {OrganizationUserStatus} from "@/Enums/OrganizationUserStatus";


export default function PersonSlider({organization, person, dispatch}) {

    const [show, setShow] = useState(false);
    const isEdit = !!person?.id;
    const [adminSwitch, setAdminSwitch] = useState(false);
    const [sliderOpen, setSliderOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [isUniversalAdmin, setIsUniversalAdmin] = useState(false);
    const [isOrganizationOwner, setIsOrganizationOwner] = useState(false);

    const isCurrentUser = usePage().props.auth.user.uuid === person?.id;

    const {data, setData, post, put, processing, errors, clearErrors, reset} = useForm({
        id: null,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        universalAdmin: false,
        subOrganizationAdmin: false,
        modules: [],
        disableAccess: false,
    });

    function closePerson() {
        dispatch({type: "showPersonSlider", payload: {showPersonSlider: false}});

        /* Needed in order to animate the slider close */
        setTimeout(function () {
            dispatch({type: "personSliderReady", payload: {personSliderReady: false}});
        }, 500);
    }

    function closeSlider() {
        setShow(false);
        clearErrors();
        closePerson();
    }

    const onChange = (event, value) => {
        if (isOrganizationOwner && (event == "disableAccess")) {
            return;
        }
        setData(event, value);
    }

    const toggleUniversalAdmin = (event, value) => {
        setAdminSwitch(value ? false : true);
        onChange(event, value);
    }

    const toggleSubOrganizationAdmin = (event, value) => {
        onChange(event, value);
    }

    function toggleModules() {
        const checkedModuleNodes = document.querySelectorAll("input[name='modules[]']:checked");
        setData('modules',
            Array.from(checkedModuleNodes, x => x.id));
    }

    const save = (e) => {
        e.preventDefault();

        if (isEdit) {
            if (isUniversalAdmin && isCurrentUser && data.universalAdmin === false) {
                put(route('manage.people.update.to-dashboard', {user: person}), {});
            } else {
                put(route('manage.people.update', {user: person}), {
                    onSuccess: () => {
                        closeSlider();
                        dispatch({type: "refresh", payload: {refresh: true}});
                        window.history.replaceState(null, null, '/manage/people');
                    }
                });
            }
        } else {
            post(route('organization.invite.store'), {
                onSuccess: () => {
                    closeSlider();
                    dispatch({type: "refresh", payload: {refresh: true}});
                },
                onError: (errors) => {
                    if (errors.email?.length && errors.email === 'This user already belongs to the organization.') {
                        closeSlider();
                    }
                }
            });
        }
    };

    const resendInvite = (e) => {
        put(route('organization.invite.resend', {user: person, organization: organization}), {
            onSuccess: () => {
                closeSlider();
            },
        });
    };

    function deletePerson() {
        dispatch({type: "refresh", payload: {refresh: true}});
        closeSlider();
    }

    const fetchEditUserProfile = () => {
        axios.get(bffApiUrlService.getEditUserEndpoint(person.id)).then(
            response => {
                let userData = response.data;
                handleEditUserProfileResponseData(userData);
                setIsUniversalAdmin(userData.universal_admin);
                setIsOrganizationOwner(userData.organization_owner);
                setShow(true);
            }
        ).catch(error => {
            console.log(error);
        });
    }

    const handleEditUserProfileResponseData = (userData) => {
        setData({
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            email: userData.email,
            phone: userData.phone,
            universalAdmin: userData.universal_admin,
            subOrganizationAdmin: userData.sub_organization_admin,
            modules: userData.modules ? userData.modules.map(module => module.uuid) :
                organization.modules.filter(module => module.type === 'Unity').map(module => module.uuid),
            disableAccess: userData.status.current === 'disabled',
        });
    }

    useEffect(() => {
        if (isEdit) {
            fetchEditUserProfile();
        } else {
            setShow(true);
        }
    }, []);

    useEffect(() => {
        if (data.universalAdmin) {
            setData('modules', organization.modules.filter(module => module.type === 'Unity').map(module => module.uuid));
        } else if (adminSwitch) {
            setAdminSwitch(false);
            setData('modules', organization.modules.filter(module => module.type === 'Unity').map(module => module.uuid));
        }

    }, [data.universalAdmin])

    useEffect(() => {
        /* Animate the opening and closing of the slider */
        if (show !== sliderOpen) {
            setSliderOpen(!sliderOpen);
        }

    }, [show])

    return (
        <Fragment>
            {sliderOpen && <div className={"fade modal-backdrop slider-backdrop show"}/>}
            <div className={sliderOpen ? "add-person slider show" : "add-person slider"}>
                <div className="slider-header">
                    <div
                        className="slider-title">{i18n.t(isEdit ? "person-slider.edit-person" : "person-slider.add-new-person")}</div>
                    <div className="close-slider">
                        <i onClick={() => closeSlider()} className="fa fa-times"></i>
                    </div>
                </div>
                <form onSubmit={save}>
                    <div className="slider-body">
                        <div className="row">
                            <ValidationErrors errors={errors["user"] ? {user: errors["user"]} : {}}></ValidationErrors>
                            <FormInput error={errors["firstName"]} name="firstName" isFocused={true}
                                       autoComplete="firstName"
                                       value={data.firstName} onChange={onChange}
                                       label={i18n.t("person-slider.first-name")} className="col-12"
                                       onKeyUp={e => e.keyCode === 13} dataTestid="person_slider-input-first_name"/>
                        </div>
                        <div className="row">
                            <FormInput error={errors["lastName"]} name="lastName" autoComplete="lastName"
                                       value={data.lastName}
                                       onChange={onChange} label={i18n.t("person-slider.last-name")} className="col-12"
                                       onKeyUp={e => e.keyCode === 13} dataTestid="person_slider-input-last_name"/>
                        </div>
                        {!isEdit &&
                            <div className="row">
                                <FormInput error={errors["email"]} name="email" autoComplete="email" value={data.email}
                                           onChange={onChange}
                                           label={i18n.t("person-slider.email")} className="col-12"
                                           onKeyUp={e => e.keyCode === 13} dataTestid="person_slider-input-email"/>
                            </div>
                        }
                        {!isEdit &&
                            <div className="row">
                                <PhoneOrEmailInput error={errors["phone"]} name="phone" autoComplete="phone"
                                                   value={data.phone} onChange={onChange}
                                                   label={i18n.t("person-slider.phone")} className="col-12"
                                                   onKeyUp={e => e.keyCode === 13} dataTestid="person_slider-input-phone"/>
                            </div>
                        }
                        <div className="row">
                            <FormCheckbox name="universalAdmin" label={i18n.t("person-slider.make-universal-admin")}
                                          onChange={toggleUniversalAdmin} value={data.universalAdmin} className="col-12"
                                          disabled={isOrganizationOwner}
                                          dataTestid="person_slider-checkbox-make_universal_admin"/>
                        </div>
                        {organization.has_child_orgs &&
                            <div className="row">
                                <FormCheckbox name="subOrganizationAdmin"
                                              label={i18n.t("person-slider.grant-suborganization-access")}
                                              onChange={toggleSubOrganizationAdmin} value={data.subOrganizationAdmin}
                                              className="col-12"
                                              dataTestid="person_slider-checkbox-make_organizational_user"/>
                            </div>
                        }

                        <div className="row modules">
                            {organization.modules.filter(module => module.type === 'Unity').map(module =>
                                <div key={module.uuid} className="col-12 col-sm-4 mb-4">
                                    <ModuleCard
                                        module={module}
                                        toggleModules={toggleModules}
                                        interactive={!data.universalAdmin}
                                        startingState={(data.modules.filter(userModule => userModule === module.uuid)).length > 0}
                                    ></ModuleCard>
                                </div>
                            )}
                        </div>
                        {isEdit && person &&
                            <DeletePerson person={person} isOpen={deleteOpen} setIsOpen={setDeleteOpen}
                                          onSuccess={deletePerson}/>
                        }
                    </div>
                    <div className="slider-footer complex">
                        {isEdit &&
                            <div className="align-left">
                                <div className="toggle-checkbox">
                                    <OnOffSwitch name="disableAccess" isToggleOn={data.disableAccess}
                                                 onChange={onChange} dataTestid="person_slider-switch-disable_access"/>
                                    <span>{i18n.t("person-slider.disable-access")}</span>
                                </div>
                            </div>
                        }
                        <div className="align-right">
                            {isEdit && !isOrganizationOwner &&
                                <Button dataTestid="person_slider-button-delete" type="button" className="btn btn-sm btn-outline-danger"
                                        onClick={() => setDeleteOpen(true)}>
                                    {i18n.t("person-slider.delete")}
                                </Button>
                            }
                            {isEdit && isOrganizationOwner &&
                                <Button dataTestid="person_slider-button-delete" type="button" processing={true} className="btn btn-sm btn-outline-secondary">
                                    {i18n.t("person-slider.delete")}
                                </Button>
                            }
                            {isEdit && person.status === OrganizationUserStatus.INVITED &&
                                <Button dataTestid="person_slider-button-resend_invite" type="button" className="btn btn-sm btn-outline-secondary"
                                        onClick={resendInvite}
                                        value="Resend Invite">
                                    {i18n.t("person-slider.resend-invite")}
                                </Button>
                            }
                            {!isEdit &&
                                <Button dataTestid="person_slider-button-cancel" type="button" className="btn btn-sm btn-outline-secondary" onClick={closeSlider}
                                        value="Cancel">
                                    {i18n.t("person-slider.cancel")}
                                </Button>
                            }
                            <Button dataTestid="person_slider-button-save" onClick={save} className="btn btn-sm btn-primary" processing={processing}>
                                {i18n.t(isEdit ? "person-slider.save" : "person-slider.invite")}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
}
